import React, { useEffect, useState } from 'react';
import {
  Card, Form, Input, Select, Switch, Button, Space, Table, Popconfirm,
  message, Divider, Alert, Collapse, Tooltip, Modal,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, InfoCircleOutlined, SaveOutlined,
} from '@ant-design/icons';
import { orderCheckAPI, botAPI, groupAPI } from '../services/api';

const { TextArea } = Input;
const { Panel } = Collapse;

interface BotOption {
  id: number;
  bot_name: string;
  bot_username: string;
  bot_type: string;
}

interface GroupOption {
  id: number;
  group_id: number;
  group_title: string;
}

interface CheckGroupMapping {
  id: number;
  bot_id: number;
  group_id: number;
  group_title: string | null;
}

interface PaymentGroupMapping {
  id: number;
  bot_id: number;
  channel_name: string;
  payment_group_id: number;
  payment_group_title: string | null;
}

interface ReplyKeyword {
  keyword: string;
  reply: string;
}

const DEFAULT_TEMPLATES = {
  msgChecking: '加急查询中，请稍等...\n商户订单号：{order_no}\n支付订单号：{payment_order_no}',
  msgPaid: '订单 {order_no} 状态：订单已支付，即将补发商户回调',
  msgPending: '{payment_order_no}',
  msgApiError: '查询订单 {order_no} 失败，请稍后重试',
  msgNoChannelMatch: '订单 {order_no} 的通道未配置对应查单群',
};

const DEFAULT_REPLY_KEYWORDS: ReplyKeyword[] = [
  { keyword: '已支付', reply: '' },
  { keyword: '未付款', reply: '' },
  { keyword: '单图不符', reply: '' },
  { keyword: '恶意刷单', reply: '' },
];

const OrderCheckSettings: React.FC = () => {
  const [form] = Form.useForm();
  const [bots, setBots] = useState<BotOption[]>([]);
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [selectedBotId, setSelectedBotId] = useState<number | undefined>();
  const [checkGroups, setCheckGroups] = useState<CheckGroupMapping[]>([]);
  const [paymentGroups, setPaymentGroups] = useState<PaymentGroupMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configExists, setConfigExists] = useState(false);

  const [addPaymentModalVisible, setAddPaymentModalVisible] = useState(false);
  const [addPaymentForm] = Form.useForm();

  const [addCheckGroupModalVisible, setAddCheckGroupModalVisible] = useState(false);
  const [addCheckGroupForm] = Form.useForm();

  // 关键字列表状态
  const [replyKeywords, setReplyKeywords] = useState<ReplyKeyword[]>(DEFAULT_REPLY_KEYWORDS);

  useEffect(() => {
    loadBots();
  }, []);

  useEffect(() => {
    if (selectedBotId) {
      loadConfig();
      loadCheckGroups();
      loadPaymentGroups();
      loadGroups();
    }
  }, [selectedBotId]);

  const loadBots = async () => {
    try {
      const res = await botAPI.getAll();
      const orderCheckBots = (res.data || []).filter(
        (b: BotOption) => b.bot_type === 'order_check'
      );
      setBots(orderCheckBots);
      if (orderCheckBots.length > 0 && !selectedBotId) {
        setSelectedBotId(orderCheckBots[0].id);
      }
    } catch {
      message.error('加载机器人列表失败');
    }
  };

  const loadGroups = async () => {
    try {
      const res = await groupAPI.getAll(selectedBotId);
      setGroups(res.data || []);
    } catch {
      // ignore
    }
  };

  const loadConfig = async () => {
    if (!selectedBotId) return;
    setLoading(true);
    try {
      const res = await orderCheckAPI.getConfig(selectedBotId);
      const config = res.data;
      if (config) {
        setConfigExists(true);
        form.setFieldsValue({
          enabled: !!config.enabled,
          orderRegex: config.order_regex,
          msgChecking: config.msg_checking,
          msgPaid: config.msg_paid,
          msgPending: config.msg_pending,
          msgApiError: config.msg_api_error,
          msgNoChannelMatch: config.msg_no_channel_match,
        });
        setReplyKeywords(
          config.reply_keywords && config.reply_keywords.length > 0
            ? config.reply_keywords
            : DEFAULT_REPLY_KEYWORDS
        );
      } else {
        setConfigExists(false);
        form.resetFields();
        form.setFieldsValue({
          enabled: false,
          orderRegex: '[A-Za-z0-9]{10,32}',
          ...DEFAULT_TEMPLATES,
        });
        setReplyKeywords(DEFAULT_REPLY_KEYWORDS);
      }
    } catch {
      message.error('加载配置失败');
    } finally {
      setLoading(false);
    }
  };

  const loadCheckGroups = async () => {
    if (!selectedBotId) return;
    try {
      const res = await orderCheckAPI.getCheckGroups(selectedBotId);
      setCheckGroups(res.data || []);
    } catch {
      // ignore
    }
  };

  const loadPaymentGroups = async () => {
    if (!selectedBotId) return;
    try {
      const res = await orderCheckAPI.getPaymentGroups(selectedBotId);
      setPaymentGroups(res.data || []);
    } catch {
      // ignore
    }
  };

  const handleSave = async () => {
    if (!selectedBotId) return;
    setSaving(true);
    try {
      const values = await form.validateFields();

      // 过滤掉空关键字
      const validKeywords = replyKeywords.filter(k => k.keyword.trim());

      await orderCheckAPI.saveConfig(selectedBotId, {
        enabled: values.enabled || false,
        orderRegex: values.orderRegex,
        msgChecking: values.msgChecking,
        msgPaid: values.msgPaid,
        msgPending: values.msgPending,
        msgApiError: values.msgApiError,
        msgNoChannelMatch: values.msgNoChannelMatch,
        replyKeywords: validKeywords,
      });

      setConfigExists(true);
      message.success('保存成功');
    } catch (err: any) {
      if (err?.errorFields) {
        message.error('请检查表单填写是否完整');
      } else {
        message.error(err?.response?.data?.error || '保存失败');
      }
    } finally {
      setSaving(false);
    }
  };

  // 查单群操作
  const handleAddCheckGroup = async () => {
    if (!selectedBotId) return;
    try {
      const values = await addCheckGroupForm.validateFields();
      await orderCheckAPI.addCheckGroup(selectedBotId, {
        groupId: parseInt(values.groupId),
        groupTitle: values.groupTitle || undefined,
      });
      message.success('添加成功');
      setAddCheckGroupModalVisible(false);
      addCheckGroupForm.resetFields();
      loadCheckGroups();
    } catch (err: any) {
      if (!err?.errorFields) {
        message.error(err?.response?.data?.error || '添加失败');
      }
    }
  };

  const handleDeleteCheckGroup = async (id: number) => {
    try {
      await orderCheckAPI.deleteCheckGroup(id);
      message.success('删除成功');
      loadCheckGroups();
    } catch {
      message.error('删除失败');
    }
  };

  // 支付群操作
  const handleAddPaymentGroup = async () => {
    if (!selectedBotId) return;
    try {
      const values = await addPaymentForm.validateFields();
      await orderCheckAPI.addPaymentGroup(selectedBotId, {
        channelName: values.channelName,
        paymentGroupId: parseInt(values.paymentGroupId),
        paymentGroupTitle: values.paymentGroupTitle || undefined,
      });
      message.success('添加成功');
      setAddPaymentModalVisible(false);
      addPaymentForm.resetFields();
      loadPaymentGroups();
    } catch (err: any) {
      if (!err?.errorFields) {
        message.error(err?.response?.data?.error || '添加失败');
      }
    }
  };

  const handleDeletePaymentGroup = async (id: number) => {
    try {
      await orderCheckAPI.deletePaymentGroup(id);
      message.success('删除成功');
      loadPaymentGroups();
    } catch {
      message.error('删除失败');
    }
  };

  // 关键字操作
  const handleAddKeyword = () => {
    setReplyKeywords([...replyKeywords, { keyword: '', reply: '' }]);
  };

  const handleRemoveKeyword = (index: number) => {
    setReplyKeywords(replyKeywords.filter((_, i) => i !== index));
  };

  const handleKeywordChange = (index: number, field: 'keyword' | 'reply', value: string) => {
    const updated = replyKeywords.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setReplyKeywords(updated);
  };

  const checkGroupColumns = [
    {
      title: '查单群 ID',
      dataIndex: 'group_id',
      key: 'group_id',
    },
    {
      title: '查单群名称',
      dataIndex: 'group_title',
      key: 'group_title',
      render: (text: string) => text || '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: CheckGroupMapping) => (
        <Popconfirm title="确定删除？" onConfirm={() => handleDeleteCheckGroup(record.id)}>
          <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
        </Popconfirm>
      ),
    },
  ];

  const paymentGroupColumns = [
    {
      title: '通道名称',
      dataIndex: 'channel_name',
      key: 'channel_name',
    },
    {
      title: '支付群 ID',
      dataIndex: 'payment_group_id',
      key: 'payment_group_id',
    },
    {
      title: '支付群名称',
      dataIndex: 'payment_group_title',
      key: 'payment_group_title',
      render: (text: string) => text || '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: PaymentGroupMapping) => (
        <Popconfirm title="确定删除？" onConfirm={() => handleDeletePaymentGroup(record.id)}>
          <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
        </Popconfirm>
      ),
    },
  ];

  const keywordColumns = [
    {
      title: (
        <Space>
          匹配正则
          <Tooltip title="支持正则表达式，例如：已支付|已付款 可同时匹配两个词。简单文字也可直接填写">
            <InfoCircleOutlined />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'keyword',
      key: 'keyword',
      render: (_: string, __: ReplyKeyword, index: number) => (
        <Input
          value={replyKeywords[index].keyword}
          onChange={(e) => handleKeywordChange(index, 'keyword', e.target.value)}
          placeholder="正则表达式，如：已支付|已付"
        />
      ),
    },
    {
      title: (
        <Space>
          转发内容（留空则原样转发）
          <Tooltip title="当支付群有人回复查单消息且内容匹配正则时，如果此处填写了内容，则转发此处的内容到查单群；留空则将原始回复内容转发">
            <InfoCircleOutlined />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'reply',
      key: 'reply',
      render: (_: string, __: ReplyKeyword, index: number) => (
        <Input
          value={replyKeywords[index].reply}
          onChange={(e) => handleKeywordChange(index, 'reply', e.target.value)}
          placeholder="留空则原样转发回复内容"
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_: any, __: ReplyKeyword, index: number) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveKeyword(index)}
        />
      ),
    },
  ];

  return (
    <div>
      <Card
        title="查单功能设置"
        extra={
          <Space>
            <span>选择查单机器人：</span>
            <Select
              style={{ width: 220 }}
              value={selectedBotId}
              onChange={(val) => setSelectedBotId(val)}
              placeholder="请选择机器人"
            >
              {bots.map(b => (
                <Select.Option key={b.id} value={b.id}>
                  {b.bot_name} {b.bot_username ? `(@${b.bot_username})` : ''}
                </Select.Option>
              ))}
            </Select>
          </Space>
        }
      >
        {!selectedBotId ? (
          <Alert message="请先创建一个查单类型(order_check)的机器人" type="info" showIcon />
        ) : (
          <Form
            form={form}
            layout="vertical"
            disabled={loading}
          >
            {/* 基础设置 */}
            <Divider orientation="left">基础设置</Divider>

            <Form.Item name="enabled" label="启用查单功能" valuePropName="checked">
              <Switch checkedChildren="开启" unCheckedChildren="关闭" />
            </Form.Item>

            <Form.Item
              name="orderRegex"
              label="订单号正则表达式"
              tooltip="用于从图片 caption 中提取订单号，默认匹配10-32位字母数字"
            >
              <Input placeholder="[A-Za-z0-9]{10,32}" />
            </Form.Item>

            <Alert
              message="三方查单接口已硬编码，API Key 从环境变量 ORDERCHECK_API_KEY 读取，接口地址从 ORDERCHECK_API_URL 读取（默认 http://localhost:2020/api/telegram/order）"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            {/* 查单群列表 */}
            <Divider orientation="left">
              查单群
              <Tooltip title="配置查单机器人监听的查单群，支持多个查单群。用户在查单群发送图片+订单号即可触发查单">
                <InfoCircleOutlined style={{ marginLeft: 8 }} />
              </Tooltip>
            </Divider>

            <Table
              dataSource={checkGroups}
              columns={checkGroupColumns}
              rowKey="id"
              size="small"
              pagination={false}
              locale={{ emptyText: '暂无查单群' }}
            />
            {checkGroups.length < 20 && (
              <Button
                type="dashed"
                block
                icon={<PlusOutlined />}
                style={{ marginTop: 12 }}
                onClick={() => setAddCheckGroupModalVisible(true)}
              >
                添加查单群
              </Button>
            )}

            {/* 支付群映射 */}
            <Divider orientation="left">
              支付群映射
              <Tooltip title="每个机器人最多配置5个支付群，根据三方接口返回的通道名称自动匹配">
                <InfoCircleOutlined style={{ marginLeft: 8 }} />
              </Tooltip>
            </Divider>

            <Table
              dataSource={paymentGroups}
              columns={paymentGroupColumns}
              rowKey="id"
              size="small"
              pagination={false}
              locale={{ emptyText: '暂无支付群映射' }}
            />
            {paymentGroups.length < 5 && (
              <Button
                type="dashed"
                block
                icon={<PlusOutlined />}
                style={{ marginTop: 12 }}
                onClick={() => setAddPaymentModalVisible(true)}
              >
                添加支付群映射
              </Button>
            )}

            {/* 回复关键字配置 */}
            <Divider orientation="left">
              回复关键字配置
              <Tooltip title="支付群有人回复查单消息时，使用正则表达式匹配回复内容，匹配成功则转发到查单群。可配置翻译内容，留空则原样转发">
                <InfoCircleOutlined style={{ marginLeft: 8 }} />
              </Tooltip>
            </Divider>

            <Table
              dataSource={replyKeywords.map((k, i) => ({ ...k, _index: i }))}
              columns={keywordColumns}
              rowKey="_index"
              size="small"
              pagination={false}
              locale={{ emptyText: '暂无关键字' }}
            />
            <Button
              type="dashed"
              block
              icon={<PlusOutlined />}
              style={{ marginTop: 12 }}
              onClick={handleAddKeyword}
            >
              添加关键字
            </Button>

            {/* 消息模板 */}
            <Collapse style={{ marginTop: 24 }} defaultActiveKey={configExists ? [] : ['templates']}>
              <Panel header="消息模板配置" key="templates" forceRender>
                <Alert
                  message="可用变量：{order_no} 原始订单号、{payment_order_no} 支付订单号、{amount} 金额、{channel_name} 通道名"
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />

                <Form.Item name="msgChecking" label="查询中消息">
                  <TextArea rows={2} />
                </Form.Item>
                <Form.Item name="msgPaid" label="已支付回复">
                  <TextArea rows={2} />
                </Form.Item>
                <Form.Item name="msgPending" label="待支付/失败转发消息（发送到支付群的 caption）">
                  <TextArea rows={3} />
                </Form.Item>
                <Form.Item name="msgApiError" label="接口异常">
                  <TextArea rows={2} />
                </Form.Item>
                <Form.Item name="msgNoChannelMatch" label="无匹配通道">
                  <TextArea rows={2} />
                </Form.Item>
              </Panel>
            </Collapse>

            <Divider />
            <Form.Item>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
                size="large"
              >
                保存配置
              </Button>
            </Form.Item>
          </Form>
        )}
      </Card>

      {/* 添加查单群弹窗 */}
      <Modal
        title="添加查单群"
        open={addCheckGroupModalVisible}
        onOk={handleAddCheckGroup}
        onCancel={() => { setAddCheckGroupModalVisible(false); addCheckGroupForm.resetFields(); }}
        okText="添加"
        cancelText="取消"
      >
        <Form form={addCheckGroupForm} layout="vertical">
          <Form.Item
            name="groupId"
            label="查单群"
            rules={[{ required: true, message: '请选择查单群' }]}
          >
            <Select
              placeholder="选择查单群"
              showSearch
              optionFilterProp="children"
              onChange={(val) => {
                const group = groups.find(g => String(g.group_id) === String(val));
                if (group) {
                  addCheckGroupForm.setFieldValue('groupTitle', group.group_title);
                }
              }}
            >
              {groups.map(g => (
                <Select.Option key={g.group_id} value={String(g.group_id)}>
                  {g.group_title || g.group_id} (ID: {g.group_id})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="groupTitle" label="群名称（自动填入）">
            <Input disabled />
          </Form.Item>
        </Form>
      </Modal>

      {/* 添加支付群映射弹窗 */}
      <Modal
        title="添加支付群映射"
        open={addPaymentModalVisible}
        onOk={handleAddPaymentGroup}
        onCancel={() => { setAddPaymentModalVisible(false); addPaymentForm.resetFields(); }}
        okText="添加"
        cancelText="取消"
      >
        <Form form={addPaymentForm} layout="vertical">
          <Form.Item
            name="channelName"
            label="三方通道名称"
            rules={[{ required: true, message: '请输入通道名称' }]}
            tooltip="三方接口返回的 channel_name 值，用于匹配对应的支付群"
          >
            <Input placeholder="通道A" />
          </Form.Item>
          <Form.Item
            name="paymentGroupId"
            label="支付群"
            rules={[{ required: true, message: '请选择支付群' }]}
          >
            <Select
              placeholder="选择支付群"
              showSearch
              optionFilterProp="children"
              onChange={(val) => {
                const group = groups.find(g => String(g.group_id) === String(val));
                if (group) {
                  addPaymentForm.setFieldValue('paymentGroupTitle', group.group_title);
                }
              }}
            >
              {groups.map(g => (
                <Select.Option key={g.group_id} value={String(g.group_id)}>
                  {g.group_title || g.group_id} (ID: {g.group_id})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="paymentGroupTitle" label="支付群名称（自动填入）">
            <Input disabled />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OrderCheckSettings;
